import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TextInput from '../../components/TextInput';
import CKEditorComponent from '../../components/CKEditorComponent';
import FileUpload from '../../components/FileUpload';
import CheckboxInput from '../../components/CheckboxInput';
import Button from '../../components/Button';
import SkeletonLoader from '../../components/SkeletonLoader';
import serviceApi from '../../api/serviceApi';
import { useToast } from '../../contexts/ToastContext';

const EditService = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [generalError, setGeneralError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    excerpt: '',
    description: '',
    image: undefined,
    is_active: 0,
  });
  const [currentImageUrl, setCurrentImageUrl] = useState(null);
  const [serviceFound, setServiceFound] = useState(true);

  useEffect(() => {
    const fetchService = async () => {
      setLoading(true);
      setGeneralError(null);
      setValidationErrors({});
      try {
        const response = await serviceApi.getServiceByIdentifier(id);
        if (response.success && response.data) {
          const serviceToEdit = response.data;
          setFormData({
            name: serviceToEdit.name,
            excerpt: serviceToEdit.excerpt,
            description: serviceToEdit.description,
            image: undefined,
            is_active: serviceToEdit.is_active ? 1 : 0,
          });
          setCurrentImageUrl(serviceToEdit.image);
          setServiceFound(true);
        } else {
          setServiceFound(false);
          setGeneralError(response.message || 'Service not found.');
        }
      } catch (err) {
        console.error('Failed to fetch service:', err);
        setServiceFound(false);
        setGeneralError(err.response?.data?.message || 'Failed to load service. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchService();
  }, [id, addToast]);

  const handleChange = (e) => {
    const { name, value, type, files, checked } = e.target; // Destructure 'checked' here
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : (type === 'file' ? files[0] : value),
    });
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleDescriptionChange = (content) => {
    setFormData({ ...formData, description: content });
    if (validationErrors.description) {
      setValidationErrors(prev => ({ ...prev, description: undefined }));
    }
  };

  const handleImageRemove = () => {
    setFormData({ ...formData, image: null });
    setCurrentImageUrl(null);
    if (validationErrors.image) {
      setValidationErrors(prev => ({ ...prev, image: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setGeneralError(null);
    setValidationErrors({});

    const data = new FormData();
    for (const key in formData) {
      if (key === 'image') {
        if (formData[key] instanceof File) {
          data.append(key, formData[key]);
        } else if (formData[key] === null) {
          data.append(key, '');
        }
      } else {
        data.append(key, formData[key]);
      }
    }

    try {
      await serviceApi.updateService(id, data);
      addToast('Service updated successfully!', 'success');
      navigate('/services');
    } catch (err) {
      console.error('Failed to update service:', err);
      if (err.response && err.response.status === 422) {
        setValidationErrors(err.response.data.errors || {});
        addToast(err.response.data.message || 'Validation failed. Please check your input.', 'error');
      } else {
        setGeneralError(err.response?.data?.message || 'Failed to update service. Please try again.', 'error');
        addToast(err.response?.data?.message || 'Failed to update service. Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white text-center">
        <SkeletonLoader type="form" />
      </div>
    );
  }

  if (!serviceFound) {
    return (
      <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white text-center">
        <h1 className="text-3xl font-bold mb-4">Service Not Found</h1>
        <p className="text-text-gray">{generalError || 'The service you are trying to edit does not exist.'}</p>
        <Button
          onClick={() => navigate('/services')}
          className="mt-4 bg-gray-500 hover:bg-gray-600 text-white"
        >
          Go to Services List
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white">
      <h1 className="text-3xl font-bold mb-6">Edit Service</h1>
      {generalError && <div className="text-red-500 mb-4">{generalError}</div>}
      <form onSubmit={handleSubmit}>
        <TextInput
          label="Service Name"
          name="name"
          id="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="Service Name"
          error={validationErrors?.name?.[0]}
          required={true}
        />
        <TextInput
          label="Excerpt"
          name="excerpt"
          id="excerpt"
          value={formData.excerpt}
          onChange={handleChange}
          placeholder="Short description or summary"
          error={validationErrors?.excerpt?.[0]}
          required={true}
        />
        <CKEditorComponent
          label="Description"
          value={formData.description}
          onChange={handleDescriptionChange}
          error={validationErrors?.description?.[0]}
          required={true}
        />
        <FileUpload
          label="Service Image"
          name="image"
          id="image"
          onChange={handleChange}
          error={validationErrors?.image?.[0]}
          required={false} // Image is now optional
          currentFileUrl={currentImageUrl}
          onRemoveFile={handleImageRemove}
          fileType="image"
          fileName="Image"
        />
        <CheckboxInput
          label="Active"
          name="is_active"
          id="is_active"
          checked={formData.is_active}
          onChange={handleChange}
        />
        <div className="flex items-center justify-between mt-6">
          <Button
            type="submit"
            className="bg-accent-red hover:bg-red-700 text-white"
            loading={loading}
          >
            Update Service
          </Button>
          <Button
            type="button"
            onClick={() => navigate('/services')}
            className="bg-gray-500 hover:bg-gray-600 text-white"
            disabled={loading}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditService;